import { NativeAdsAdapter } from './ads/NativeAdsAdapter';

export class TheoplayerAdapter {
  adsAdapters = {
    nativeAdsAdapter: NativeAdsAdapter,
  };

  checkExistsPlayer() {
    try {
      if (this.player.element) {
        return this.checkExistsObjectOnPage(this.player.element);
      }
    } catch (err) {
      return true;
    }
  }

  getBitrate() {
    let ret = -1;
    const qty = this.getActiveQuality();
    if (qty && qty.bandwidth) {
      ret = qty.bandwidth;
    }
    return ret;
  }

  pauseListener(e: any): any {
    this.firePlayerLog('pauseListener', {});
    if (!this.flags.isSeeking) {
      this.firePause({}, 'pauseListener');
    }
  }

  offlineListener(e: any) {
    this.firePlayerLog('offlineListener', {});
    this.fireError('OFFLINE_SOURCE', 'OFFLINE_SOURCE', undefined, undefined, 'offlineListener');
  }

  getRendition() {
    this._setLanguage();
    let ret = null;
    const qty = this.getActiveQuality();
    if (qty) {
      if (qty.name && Number.isNaN(qty.name)) {
        ret = qty.name;
      } else {
        ret = this.getNpawUtils().buildRenditionString(qty.width, qty.height, qty.bandwidth);
      }
    } else if (this.player.videoHeight && this.player.videoWidth) {
      ret = this.getNpawUtils().buildRenditionString(
        this.player.videoWidth,
        this.player.videoHeight,
        0
      );
    }
    return ret;
  }

  getLatency() {
    let ret = null;
    if (this.getIsLive() && this.player.currentProgramDateTime !== null) {
      const now = new Date().getTime();
      ret = now - this.player.currentProgramDateTime;
    }
    return ret;
  }

  getPlayerVersion() {
    let version = 'THEOplayer';
    if (typeof THEOplayer !== 'undefined') {
      version += THEOplayer.version;
    } else if (typeof theoplayer !== 'undefined') {
      version += theoplayer.version;
    }
    return version;
  }

  playListener(e: any) {
    this.firePlayerLog('playListener', {});
    if (!this.player.autoplay) {
      this.fireInit({}, 'playListener');
    }
  }

  endedListener(e: any) {
    this.firePlayerLog('endedListener', {});
    if (this.flags.isStarted) {
      this.fireStop({}, 'endedListener');
      this.ended = true;
    }
  }

  sourceChangeListener(e: any) {
    this.firePlayerLog('sourceChangeListener', {});
    if (this.previousSrc !== this.player.src) {
      this.fireStop({}, 'sourceChangeListener');
      this.ended = true;
    }
  }

  getPlayrate() {
    try {
      return this.player.playbackRate;
    } catch (err) {
      /* empty */
    }
    return 0;
  }

  getActiveQuality() {
    const vTracks = this.player.videoTracks;
    for (let i = 0; i < vTracks.length; i++) {
      const currentTrack = vTracks.item(i);
      if (currentTrack.enabled) {
        return currentTrack.activeQuality;
      }
    }
    return {};
  }

  playrateListener(e: any) {
    if (this.monitor) {
      this.monitor.skipNextTick();
    }
  }

  getPlayhead() {
    let ret = null;
    try {
      if (
        (this.player.ads && this.player.ads.playing) ||
        (this.flags.isSeeking && this.flags.isPaused)
      ) {
        ret = this.lastPlayhead;
      } else {
        this.lastPlayhead = this.player.currentTime;
        ret = this.player.currentTime;
      }
    } catch (err) {
      /* empty */
    }
    return ret;
  }

  getDuration() {
    let ret = null;
    if (this.player.ads && this.player.ads.playing) {
      ret = this.lastDuration || ret;
    } else {
      this.lastDuration = this.player.duration;
      ret = this.lastDuration;
    }
    return ret;
  }

  getThroughput() {
    if (this.player) {
      if (this.player.metrics && this.player.metrics.currentBandwidthEstimate) {
        return this.player.metrics.currentBandwidthEstimate;
      }
    }
    return null;
  }

  getDroppedFrames() {
    return this.player.metrics ? this.player.metrics.droppedVideoFrames : null;
  }

  getVersion() {
    return '7.0.14-theoplayer2-jsclass';
  }

  unregisterListeners() {
    if (this.getVideo()) {
      this.getVideo().removeAdsAdapter();
    }
    if (this.monitor) this.monitor.stop();
    if (this.player && this.references) {
      for (const key of Object.keys(this.references)) {
        this.player.removeEventListener(key, this.references[key]);
      }
      delete this.references;
    }
    if (this.player && this.player.network && this.networkReferences) {
      this.player.network.removeRequestInterceptor(this.requestInterceptor);
      this.player.network.removeResponseInterceptor(this.responseInterceptor);
      for (const key2 of Object.keys(this.networkReferences)) {
        this.player.network.removeEventListener(key2, this.networkReferences[key2]);
      }
      delete this.networkReferences;
      delete this.requestInterceptor;
      delete this.responseInterceptor;
    }
  }

  stateChangeListener(e: any) {
    if (
      e.readyState === 1 &&
      this.player.src !== this.previousSrc &&
      this.player.src !== undefined
    ) {
      this.fireInit({}, 'stateChangeListener');
      this.previousSrc = this.player.src;
    }
    if (
      e.currentTime === 0 &&
      e.readyState === 0 &&
      this.player.src !== this.previousSrc &&
      this.player.src !== undefined
    ) {
      this.fireStop();
      this.previousSrc = this.player.src;
    }
  }

  _segmentUrl() {
    let ret = null;
    if (
      this.getVideo() &&
      this.getVideo().options &&
      !this.getVideo().options['parse.manifest'] &&
      this.parsedUrl
    ) {
      ret = {
        parsedResource: this.parsedUrl,
      };
    }
    return ret;
  }

  getIsLive() {
    return Number.isNaN(this.player.duration) ? null : this.player.duration === Infinity;
  }

  _setLanguage() {
    if (this.getVideo()) {
      const tracks = this.player.audioTracks;
      for (const track of Object.keys(tracks)) {
        const t = tracks[track];
        if (t.activeQuality) {
          this.getVideo().options['content.language'] =
            t.language === 'und' ? t.name || t.label : t.language;
        }
      }
    }
  }

  registerListeners() {
    this.getNpawUtils().logAllEvents(this.player);
    this.monitorPlayhead(false, true);
    this.references = {
      play: this.playListener.bind(this),
      playing: this.playingListener.bind(this),
      pause: this.pauseListener.bind(this),
      seeking: this.seekingListener.bind(this),
      ended: this.endedListener.bind(this),
      error: this.errorListener.bind(this),
      readystatechange: this.stateChangeListener.bind(this),
      sourcechange: this.sourceChangeListener.bind(this),
      timeupdate: this.timeUpdateListener.bind(this),
      ratechange: this.playrateListener.bind(this),
      waiting: this.waitingListener.bind(this),
    };
    this.networkReferences = {
      offline: this.offlineListener.bind(this),
    };
    for (const key of Object.keys(this.references)) {
      this.player.addEventListener(key, this.references[key]);
    }
    if (this.player.network) {
      for (const key2 of Object.keys(this.networkReferences)) {
        this.player.network.addEventListener(key2, this.networkReferences[key2]);
      }
    }
    if (!this.getVideo().getAdsAdapter() && this.player.ads) {
      this.getAdapterClass('nativeAdsAdapter')._updatePlayer(this.player);
      this.plugin.setAdsAdapter(
        this.getAdapterClass('nativeAdsAdapter'),
        this.getVideo().getVideoKey()
      );
    }
  }

  getResource() {
    if (this.player.src && !this.previousSrc) {
      this.previousSrc = this.player.src;
    }
    return this.player.src || null;
  }

  getURLToParse() {
    return this.parsedUrl;
  }

  seekingListener(e: any) {
    this.firePlayerLog('seekingListener', {});
    this.fireSeekBegin({}, false, 'seekingListener');
  }

  timeUpdateListener(e: any) {
    if (!this.player.ads || !this.player.ads.playing) {
      if (!this.flags.isStarted && e.currentTime && !this.ended) {
        if (this.getIsLive() || e.currentTime + 1 < this.getDuration()) {
          this.fireStart(this._segmentUrl(), 'timeUpdateListener');
          this.fireJoin(this._segmentUrl(), 'timeUpdateListener');
        }
      }
      if (!this.flags.isPaused) {
        this.fireSeekEnd({}, 'timeUpdateListener');
        this.fireBufferEnd({}, 'timeUpdateListener');
      }
    }
  }

  getPlayerName() {
    return 'THEOplayer';
  }

  playingListener(e: any) {
    this.firePlayerLog('playingListener', {});
    if (!this.getVideo().getAdsAdapter() || !this.getVideo().getAdsAdapter().flags.isStarted) {
      if (this.flags.isSeeking && !this.flags.isPaused) {
        this.fireSeekEnd({}, 'playingListener');
      }
      if (this.flags.isBuffering && !this.flags.isPaused) {
        this.fireBufferEnd({}, 'playingListener');
      }
      this.fireResume({}, 'playingListener');
      if (!this.player.ads || !this.player.ads.playing) {
        this.ended = false;
        this.fireStart(this._segmentUrl(), 'playingListener');
      }
      this.fireJoin({}, 'playingListener');
    }
  }

  waitingListener(e: any) {
    this.firePlayerLog('waitingListener', {});
    if (!this.flags.isPaused && !this.flags.isSeeking) {
      this.fireBufferBegin({}, false, 'waitingListener');
    }
  }

  errorListener(e: any) {
    this.firePlayerLog('errorListener', {});
    let eventErrorObjectCode;
    let playerErrorObjectCode;
    let playerErrorCode;
    let eventErrorObjectMessage;
    let playerErrorObjectMessage;
    let playerErrorMessage;
    let eventErrorObjectCause;
    let playerErrorObjectCause;
    let playerErrorCause;
    if (e.errorObject) {
      eventErrorObjectCode = e.errorObject.code;
      eventErrorObjectMessage = e.errorObject.message;
      eventErrorObjectCause = !!e.errorObject.cause && e.errorObject.cause.message;
    } else {
      eventErrorObjectMessage = e.error;
    }
    if (this.player && this.player.errorObject) {
      playerErrorObjectCode = this.player.errorObject.code;
      playerErrorObjectMessage = this.player.errorObject.message;
      playerErrorObjectCause =
        !!this.player.errorObject.cause && this.player.errorObject.cause.message;
    }
    if (this.player && this.player.error) {
      playerErrorCode = this.player.error.code;
      playerErrorMessage = this.player.error.message;
      playerErrorCause = this.player.error.cause;
    }
    const messages = [];
    if (eventErrorObjectMessage) {
      messages.push(`event: ${eventErrorObjectMessage}`);
    }
    if (playerErrorObjectMessage) {
      messages.push(`player.errorObject: ${playerErrorObjectMessage}`);
    }
    if (playerErrorMessage) {
      messages.push(`player.error: ${playerErrorMessage}`);
    }
    const causes = [];
    if (eventErrorObjectCause) {
      causes.push(`event: ${eventErrorObjectCause}`);
    }
    if (playerErrorObjectCause) {
      causes.push(`player.errorObject: ${playerErrorObjectCause}`);
    }
    if (playerErrorCause) {
      causes.push(`player.error: ${playerErrorCause}`);
    }
    const codes = [];
    if (eventErrorObjectCode) {
      codes.push(`event: ${eventErrorObjectCode}`);
    }
    if (playerErrorObjectCode) {
      codes.push(`player.errorObject: ${playerErrorObjectCode}`);
    }
    if (playerErrorCode) {
      codes.push(`player.error: ${playerErrorCode}`);
    }
    const code =
      eventErrorObjectCode || playerErrorObjectCode || playerErrorCode || 'GENERIC_ERROR';
    const msg = `[MESSAGES] ${messages.join(' - ')} - [CAUSES] ${causes.join(
      ' - '
    )} - [CODES] ${codes.join(' - ')}`;
    this.fireFatalError(code, msg, undefined, undefined, 'errorListener');
    this.fireStop({}, 'errorListener');
    this.ended = true;
  }
}

export declare interface TheoplayerAdapter {
  player: any;
  firePlayerLog: any;
  flags: any;
  checkExistsObjectOnPage: any;
  firePause: any;
  fireError: any;
  getNpawUtils: any;
  fireInit: any;
  fireStop: any;
  ended: boolean;
  previousSrc: string;
  monitor: any;
  lastPlayhead: any;
  lastDuration: any;
  getVideo: any;
  references: any;
  networkReferences: any;
  requestInterceptor: any;
  responseInterceptor: any;
  parsedUrl: any;
  monitorPlayhead: any;
  getAdapterClass: any;
  plugin: any;
  fireSeekBegin: any;
  fireStart: any;
  fireJoin: any;
  fireSeekEnd: any;
  fireBufferEnd: any;
  fireResume: any;
  fireBufferBegin: any;
  fireFatalError: any;
}

declare const THEOplayer: any;
declare const theoplayer: any;
